// Small pure helpers for the Entities tab, extracted so the headline-count
// rules are unit-testable without standing up the full tab (query client +
// API mocks).

import type { CountryOption, FacetCounts } from "@/lib/types";

// IR-1414: the Entities tab's headline number counted every row the endpoint
// returns — and the endpoint's base set is `type != 'group'` with NO status
// filter, so soft-deleted entities (status='5') were in it. On prod that read
// 1,365,942 while the IRIS dashboard's Statistics gauge read 1.1M, because the
// dashboard serves `total_active_entities` (status='1' AND type!='group').
// The two numbers reconcile exactly: 1,128,232 active + 238,190 deleted =
// 1,366,422. Nothing was stale (the rollup was stamped the same morning) and
// nothing was missing — they were counting different populations, and Tomer
// reasonably read the gap as data loss.
//
// So the headline now leads with the live population (everything that is not
// deleted, matching what IRIS shows an analyst) and carries the deleted count
// beside it, rather than silently folding the two together. The table itself
// still lists deleted rows — ops needs to see and restore them — and the Status
// filter still reaches them.
export interface CountHeadline {
  /** The big number: live (non-deleted) entities when we can tell them apart. */
  primary: number;
  /** Soft-deleted entities, or null when there is nothing to disclose. */
  deleted: number | null;
}

// `facets` come from the endpoint's UNFILTERED base set (`_entity_facets`
// recomputes from `Entity.objects.exclude(type='group')` and ignores the query
// params), so they describe the whole table and cannot be used to break down a
// filtered view. Under any active filter the headline therefore stays exactly
// what it has always been — the filtered total, undecorated. Splitting it with
// a whole-table deleted count would state a falsehood about the current view.
export function entityCountHeadline(
  total: number,
  statusFacets: FacetCounts | undefined,
  isFiltered: boolean,
): CountHeadline {
  if (isFiltered) return { primary: total, deleted: null };

  const deleted = statusFacets?.deleted ?? 0;
  // A missing/zero deleted facet means there is nothing to disclose — show the
  // total as-is rather than a "· 0 deleted" that reads like a broken widget.
  if (!deleted) return { primary: total, deleted: null };

  // Derive the live count by subtraction rather than reading the `active`
  // facet: `not deleted` is the population IRIS's dashboard shows, and it also
  // covers the 'inactive' bucket ('0'), which exists in the status map even
  // though prod currently holds no such entity rows. Clamped at 0 so a facet
  // that ever disagrees with `total` degrades to a plain number instead of a
  // negative one.
  return { primary: Math.max(0, total - deleted), deleted };
}

/**
 * IR-1415: turn the endpoint's `country_options` into dropdown options.
 *
 * The backend caps the list at the 50 biggest countries. A code the operator
 * has already selected can therefore fall out of that window once other
 * filters narrow the set -- and an option that is not in the menu cannot be
 * unticked, which strands the table on a filter the operator can see the chip
 * for but not remove. Selected codes are appended back for that reason.
 */
export function buildCountryOptions(
  served: CountryOption[] | undefined,
  selected: string[],
): { value: string; label: string }[] {
  const options = (served ?? []).map((o) => ({
    value: o.code,
    label: `${o.name} (${o.count.toLocaleString()})`,
  }));
  const present = new Set(options.map((o) => o.value));
  for (const code of selected) {
    if (!present.has(code)) options.push({ value: code, label: code });
  }
  return options;
}
